// Paddle SDK loaded via CDN script tag (no npm package needed)

// Paddle configuration
const PADDLE_CONFIG = {
  vendorId: 'live_8f0de795054472df4c9107e38d2',
  environment: 'production' as 'sandbox' | 'production',
  productId: '',
  priceId: 'pri_01ketn21vtch535pqbt248r60p',
  // TODO: Replace with your actual success/cancel URLs
  successUrl: 'https://maestroconnect.io/payment-success',
  cancelUrl: 'https://maestroconnect.io/teacher-dashboard',
}

// Type for the global Paddle object loaded via CDN
interface PaddleInstance {
  Initialize: (config: { token: string; environment?: string }) => void
  Checkout: {
    open: (config: any) => void
  }
  CustomerPortal: {
    open: (config: any) => void
  }
}

declare global {
  interface Window {
    Paddle?: PaddleInstance
  }
}

let paddleInitialized = false

/**
 * Load Paddle.js script from CDN
 */
const loadPaddleScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.querySelector('script[src*="paddle.com"]')) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Paddle script'))
    document.head.appendChild(script)
  })
}

/**
 * Initialize Paddle SDK
 */
export const initPaddle = async (): Promise<PaddleInstance | null> => {
  if (paddleInitialized && window.Paddle) return window.Paddle

  try {
    await loadPaddleScript()

    if (!window.Paddle) {
      throw new Error('Paddle not available after script load')
    }

    window.Paddle.Initialize({
      token: PADDLE_CONFIG.vendorId,
    })

    paddleInitialized = true
    console.log('[Paddle] SDK initialized successfully')
    return window.Paddle
  } catch (error) {
    console.error('[Paddle] Failed to initialize SDK:', error)
    return null
  }
}

/**
 * Open Paddle Checkout for new subscription
 */
export const openPaddleCheckout = async (
  userEmail: string,
  userId: string,
  userName: string,
): Promise<void> => {
  const paddle = await initPaddle()
  if (!paddle) {
    throw new Error('Paddle SDK not initialized')
  }

  try {
    console.log('[Paddle] Opening checkout for user:', userId)

    paddle.Checkout.open({
      items: [
        {
          priceId: PADDLE_CONFIG.priceId,
        },
      ],
      customer: {
        email: userEmail,
      },
      customData: {
        userId: userId,
        userName: userName,
      },
      settings: {
        displayMode: 'overlay',
        theme: 'light',
        locale: 'en',
        successUrl: `${PADDLE_CONFIG.successUrl}?userId=${userId}`,
      },
    })
  } catch (error) {
    console.error('[Paddle] Checkout error:', error)
    throw error
  }
}

/**
 * Open Paddle Customer Portal (for managing subscription)
 */
export const openPaddlePortal = async (
  paddleCustomerId: string,
): Promise<void> => {
  const paddle = await initPaddle()
  if (!paddle) {
    throw new Error('Paddle SDK not initialized')
  }

  try {
    console.log('[Paddle] Opening customer portal for:', paddleCustomerId)

    paddle.CustomerPortal.open({
      customerId: paddleCustomerId,
    })
  } catch (error) {
    console.error('[Paddle] Portal error:', error)
    throw error
  }
}

/**
 * Open Paddle Update Payment Method
 */
export const openPaddleUpdatePayment = async (
  paddleSubscriptionId: string,
): Promise<void> => {
  const paddle = await initPaddle()
  if (!paddle) {
    throw new Error('Paddle SDK not initialized')
  }

  try {
    console.log(
      '[Paddle] Opening payment update for subscription:',
      paddleSubscriptionId,
    )

    paddle.Checkout.open({
      transactionId: paddleSubscriptionId,
      settings: {
        displayMode: 'overlay',
        theme: 'light',
      },
    })
  } catch (error) {
    console.error('[Paddle] Update payment error:', error)
    throw error
  }
}

export default {
  initPaddle,
  openPaddleCheckout,
  openPaddlePortal,
  openPaddleUpdatePayment,
}
